import { Page, SortOption, FilterOption } from "@/types/search-page";
import { JumpToMenuLink } from "@/types/jumpto-menu";
import { Patch, PatchVM } from "@/types/json-patch";
import { DisbursementVM, DisbursementFormOptions } from "@/types/disbursements";
import { CommentModel } from "@/types/comments";

export enum CertificationStatuses {
  CertificationRequestPending = 1,
  SentForCertification = 2,
  Certified = 3,
  SchoolUnableToCertify = 4,
  PartiallyDisbursed = 5,
  FullyDisbursed = 6,
  Terminated = 7,
  LenderHold = 8,
  ReadyForCertification = 9,
}

export class CertificationStatusIds {
  public static get CertificationRequestPending(): number {
    return CertificationStatuses.CertificationRequestPending;
  }
  public static get SentForCertification(): number {
    return CertificationStatuses.SentForCertification;
  }
  public static get Certified(): number {
    return CertificationStatuses.Certified;
  }
  public static get SchoolUnableToCertify(): number {
    return CertificationStatuses.SchoolUnableToCertify;
  }
  public static get PartiallyDisbursed(): number {
    return CertificationStatuses.PartiallyDisbursed;
  }
  public static get FullyDisbursed(): number {
    return CertificationStatuses.FullyDisbursed;
  }
  public static get Terminated(): number {
    return CertificationStatuses.Terminated;
  }
  public static get LenderHold(): number {
    return CertificationStatuses.LenderHold;
  }
  public static get ReadyForCertification(): number {
    return CertificationStatuses.ReadyForCertification;
  }
}

export interface Response<T> {
  success: boolean;
  results: T;
  errors?: string[];
}

export interface PagedCertificationSearchResponse {
  page: Page;
  totalCount: number;
  items: CertificationSearchResultModel[];
}

export interface CertificationSearchResultModel {
  id: number;
  borrowerName: string;
  borrowerFullName: string;
  borrowerMiddleName: string;
  borrowerInitials: string;
  borrowerLastName: string;
  certificationStatusTypeDescription: string;
  certificationMethodTypeDescription: string;
  certificationStatusType: string;
  certificationMethodType: string;
  certifiedAmount?: number;
  certifiedAmountFormatted: string;
  cluid?: string;
  createdAtUtc: Date;
  drawRequestedAmount: number;
  drawRequestedAmountFormatted: string;
  lenderId: number;
  lenderName: string;
  lenderDisplayName: string;
  loanAmount: number;
  loanAmountFormatted: string;
  referenceId?: string;
}

export interface SearchAndFilterCertificationsRequest {
  search?: string | null;
  productType: number | null;
  enrollmentTermIds: number[];
  certificationMethodTypes: number[];
  certificationStatusTypes: number[];
  lenderIds: number[];
  page?: Page | null;
  sortOptions: SortOption[];
}

export interface FilterCertificationsRequest {
  certificationIds: number[];
  certificationStatusType: any | null;
  referenceId: string | null;
  borrowerIds: number[];
}

export interface CertificationFormOptions {
  schoolLevels: FilterOption[];
  enrollmentStatuses: FilterOption[];
  certificationMethods: FilterOption[];
  certificationStatuses: FilterOption[];
  enrollmentTerms: FilterOption[];
}
export interface Borrowers {
  borrower: BorrowerVM;
  coBorrower: BorrowerVM;
  coborrowerDisplayName: string;
  hasCoborrower: boolean;
}

export interface CertificationSlimVM {
  id: number;
  sequenceNumber: string;
  certifiedAmount: number;
  certifiedAmountDisplay: string;
  createdAtUtc: Date;
  certificationMethodTypeId: number;
  certificationStatusTypeId: number;
}

export interface CertificationDetails {
  referenceId: string;
  createdDate: string;
  expirationDate: string;
}
export interface CertificationVM {
  id: number;
  appId: number;
  appMovedToLoanSvc: boolean;
  borrowers: Borrowers;
  details: CertificationDetails;
	drawId: number;
  header: CertificationHeaderVM;
  loanId: number;
  productTypeId: number;
  referenceId: string;
  certifications: CertificationSlimVM[];
  initialFormValues: CertificationForm;
  form: CertificationForm;
  formOptions: CertificationFormOptions;
  disbursements: DisbursementVM[];
  disbursementFormOptions: DisbursementFormOptions;
  comments: CommentModel[];
  totalCertificationDisbursementAmount: number;
  totalCertificationDisbursementAmountDisplay: string;
  jumpToMenuLinks: JumpToMenuLink[];
	isEditable: boolean;
  canGenerateModificationFile: boolean;
  nextBusinessDay: string;
  certificationAmountForDisbursements: number;
  postDisbursementEffectiveDateDays?: number;
  // Frontend fields
  mobileHidden: boolean;
  showDetails: boolean;
}

export interface CertificationHeaderVM {
	lenderId: number;
  lenderName: string;
  ncherCode: string;
  ncherId: string;
  programName: string;
  programAltCode: string;
  loanAmountDisplay: string;
  totalDisbursedAmountDisplay: string;
}

export interface BorrowerVM {
  addressDisplay: string;
  birthdate: string;
  borrowerTypeId: number;
  borrowerTypeName: string;
  emailAddress: string;
  fullName: string;
  initials: string;
  nationalIdNumber: string;
  nationalIdNumberLabel: string;
  nationalIdNumberMasked: string;
  phoneNumberDisplay: string;
  showNationalIdNumberToggle: boolean;
  username: string;
	firstName: string;
	lastName: string;
}

export interface CertificationForm {
  id: number;
  school: string;
  cluid: string;
  requestedAmount: string;
  requestedAmountDisplay: string;
  sequenceNumber: string;
  createdDate: string;
  certifiedAmount: number;
  certifiedAmountDisplay: string;
  originalCertifiedAmount: number;
  originalCertifiedAmountDisplay: string;
  certificationMethodTypeId: number;
  certificationMethodTypeName: string;
  certificationStatusTypeId: number;
  certificationStatusTypeName: string;
  certificationSendDate: string;
  certificationSendDateDisplay: string;
  certificationReceiveDate: string;
  certificationReceiveDateDisplay: string;
  enrollmentStatusTypeId: number;
  enrollmentStatusTypeName: string;
  schoolLevelTypeId: number | null;
  schoolLevelTypeName: string;
  schoolLevelDisplay: string;
  anticipatedGraduationDateDisplay: string;
  anticipatedGraduationDate: string;
  enrollmentTermId: number;
  enrollmentTermDisplay: string;
  loanPeriodStartDate: string;
  loanPeriodStartDateDisplay: string;
  loanPeriodEndDate: string;
  loanPeriodEndDateDisplay: string;
}

export interface UpdateCertificationRequest {
  patchDocument: Patch[];
  patchesVM: PatchVM[];
  certificationPatches?: Patch[];
  drawPatches?: Patch[];
  appId: number;
  loanId: number;
	drawId: number;
  certificationId: number;
  certificationSequenceNumber: string;
}

export interface AddCommentRequest {
  certificationId: number;
  loanId: number;
  appId: number;
  comment?: string | null;
}

export interface GetCommentsRequest {
  certificationId: number;
  loanId: number;
  appId: number;
}

export interface DisbursementHistoryVM {
  entries: DisbursementHistory[];
}

export interface DisbursementHistory {  
  id: number;  
  comment: string;
  userDisplayName: string;
  createdAtFormatted: string;
  certificationId : number;
  disbursementId : number;
  postDisbursementChangeAmountLedgerId : number | null;
  sequencesText: string;
  certificationSequence: string;
  disbursementSequence: string;  
}

export type GetDisbursementHistoryRequest = GetCommentsRequest;

export interface GetDisbursementHistoryResponse {
  success: boolean;
  errors: string[];
  results: DisbursementHistoryVM;
}